import React from 'react';
import { FaUserPlus, FaPlus, FaCheck, FaTimes, FaExclamationTriangle, FaUserClock } from 'react-icons/fa';

const StatCard = ({ title, value, color }) => {
  const colors = {
    yellow: 'text-[#FFD700]',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
    green: 'text-green-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
    cyan: 'text-cyan-400'
  };
  return (
    <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-4 border border-[#2D5A8F]">
      <p className="text-gray-400 text-xs">{title}</p>
      <p className={`text-2xl font-bold ${colors[color]}`}>{value}</p>
    </div>
  );
};

const AdminOverviewTab = ({ 
  systemStats, 
  pendingRequests, 
  topUpRequests,
  pendingRegistrations,
  handleApproveAdvisorRequest, 
  handleApproveTopUp, 
  handleRejectTopUp,
  handleApproveRegistration,
  handleRejectRegistration
}) => (
  <div className="space-y-6">
    {/* Stats Cards */}
    {systemStats && (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <StatCard title="Total Users" value={systemStats.users?.total || 0} color="yellow" />
        <StatCard title="Advisors" value={systemStats.users?.advisors || 0} color="blue" />
        <StatCard title="Managers" value={systemStats.users?.managers || 0} color="purple" />
        <StatCard title="Total Clients" value={systemStats.users?.clients || 0} color="green" />
        <StatCard title="Free Clients" value={systemStats.users?.free_clients || 0} color="cyan" />
        <StatCard title="Paid Clients" value={systemStats.users?.paid_clients || 0} color="yellow" />
        <StatCard title="Pending Registrations" value={pendingRegistrations?.length || 0} color="orange" />
        <StatCard title="Top-Up Requests" value={topUpRequests?.length || 0} color="red" />
      </div>
    )}

    {/* Pending User Registrations - NEW SECTION */}
    {pendingRegistrations && pendingRegistrations.length > 0 && (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-cyan-500/50">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <FaUserClock className="mr-2 text-cyan-400" /> Pending User Registrations
          <span className="ml-2 bg-cyan-500 text-white text-xs px-2 py-1 rounded-full">
            {pendingRegistrations.length} awaiting approval
          </span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-80 overflow-y-auto">
          {pendingRegistrations.map((user) => (
            <div key={user.id} className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]" data-testid={`pending-user-${user.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-white font-medium">{user.name}</p>
                  <p className="text-gray-400 text-sm">{user.email}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.role === 'advisor' ? 'bg-blue-500/20 text-blue-400' :
                    user.role === 'manager' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {user.role}
                  </span>
                  {/* Plan badge — only meaningful for client role */}
                  {user.role === 'client' && (
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                        user.plan === 'pro_plus'
                          ? 'bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40'
                          : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                      }`}
                      data-testid={`plan-badge-${user.id}`}
                    >
                      {user.plan === 'pro_plus' ? 'PRO+ (Paid)' : 'Plus (Free)'}
                    </span>
                  )}
                </div>
              </div>

              {/* Registration source: Self-Registered or Added by Advisor/Manager */}
              <div className="mb-2" data-testid={`source-${user.id}`}>
                {user.referrer_name ? (
                  <p className="text-xs">
                    <span className="text-gray-500">Added by </span>
                    <span className="text-[#FFD700] font-medium">{user.referrer_name}</span>
                    <span className="text-gray-500"> ({user.referrer_role || 'advisor'})</span>
                  </p>
                ) : (
                  <p className="text-xs text-cyan-300 font-medium">Self-Registered</p>
                )}
              </div>

              {user.phone && (
                <p className="text-gray-500 text-xs mb-3">Phone: {user.phone}</p>
              )}
              <p className="text-gray-500 text-xs mb-3">
                Registered: {new Date(user.created_at).toLocaleDateString()}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleApproveRegistration(user)}
                  className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm flex items-center justify-center transition-colors"
                  data-testid={`approve-btn-${user.id}`}
                >
                  <FaCheck className="mr-1" /> Approve
                </button>
                <button
                  onClick={() => handleRejectRegistration(user)}
                  className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm flex items-center justify-center transition-colors"
                  data-testid={`reject-btn-${user.id}`}
                >
                  <FaTimes className="mr-1" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Pending Items */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Advisor Requests */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <FaUserPlus className="mr-2 text-yellow-400" /> Pending Advisor Requests
        </h3>
        {pendingRequests && pendingRequests.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {pendingRequests.map((req) => (
              <div key={req.id} className="bg-[#0F172A] rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{req.name}</p>
                  <p className="text-gray-400 text-sm">{req.email}</p>
                </div>
                <button
                  onClick={() => handleApproveAdvisorRequest(req)}
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                >
                  Approve
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No pending requests</p>
        )}
      </div>

      {/* Top-Up Requests */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <FaPlus className="mr-2 text-orange-400" /> Pending Top-Up Requests
        </h3>
        {topUpRequests && topUpRequests.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {topUpRequests.map((req) => (
              <div key={req.id} className="bg-[#0F172A] rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-white font-medium">{req.advisor_name}</p>
                    <p className="text-gray-400 text-xs">{req.current_limit} → {req.current_limit + req.requested_additional}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleApproveTopUp(req)} className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs">
                      <FaCheck />
                    </button>
                    <button onClick={() => handleRejectTopUp(req)} className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs">
                      <FaTimes />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No pending requests</p>
        )}
      </div>
    </div>

    {/* Capacity Alerts */}
    {systemStats && systemStats.capacity && (systemStats.capacity.advisors_at_limit > 0 || systemStats.capacity.advisors_near_limit > 0) && (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <FaExclamationTriangle className="mr-2 text-yellow-400" /> Capacity Alerts
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {systemStats.capacity.advisors_at_limit > 0 && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
              <p className="text-red-400 font-bold text-2xl">{systemStats.capacity.advisors_at_limit}</p>
              <p className="text-red-300 text-sm">Advisors at client limit</p>
            </div>
          )}
          {systemStats.capacity.advisors_near_limit > 0 && (
            <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4">
              <p className="text-yellow-400 font-bold text-2xl">{systemStats.capacity.advisors_near_limit}</p>
              <p className="text-yellow-300 text-sm">Advisors near limit (80%+)</p>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

export default AdminOverviewTab;
